import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Receipt, Truck, RefreshCcw, XCircle, CheckCircle2, Mail, Package, MapPin,
} from "lucide-react";
import {
  Button, Input, Label, Drawer, Modal, ModalFooter, Badge, EmptyState,
  Skeleton, Paginator, useToast, useConfirm, FieldHint,
} from "@/components/ui";
import { ApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import {
  storeApi, ORDER_STATUS_COLORS, ORDER_STATUS_LABELS, centsToStr, strToCents,
  type StoreOrder, type StoreAddress,
} from "./api";

const PAGE_SIZE = 25;

const FILTERS: { key: string; label: string }[] = [
  { key: "", label: "All" },
  { key: "open", label: "To fulfil" },
  { key: "pending", label: "Pending payment" },
  { key: "paid", label: "Paid" },
  { key: "enquiry", label: "Enquiries" },
  { key: "refunded", label: "Refunded" },
  { key: "canceled", label: "Canceled" },
];

function fmtDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function addressLines(a: StoreAddress | null): string[] {
  if (!a) return [];
  return [
    a.name,
    a.line1,
    a.line2,
    [a.city, a.region, a.postal_code].filter(Boolean).join(", "),
    a.country,
    a.phone,
  ].filter(Boolean) as string[];
}

export function StoreOrdersPage() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const [orders, setOrders] = useState<StoreOrder[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(() => {
    storeApi.orders({ status: status || undefined, search: search || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then((r) => { setOrders(r.orders); setTotal(r.total); })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed to load orders"));
  }, [toast, status, search, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Orders</h1>
          <p className="mt-1 text-sm text-zinc-400">Sales, enquiries, fulfilment and refunds.</p>
        </div>
        <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search email, order #, product…" className="w-64" />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => { setPage(0); setParams(f.key ? { status: f.key } : {}); }}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
              status === f.key
                ? "bg-rose-500/15 text-rose-200"
                : "bg-zinc-900/60 text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {orders === null ? (
        <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : orders.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No orders here"
          description={status ? "Nothing matches this filter yet." : "Orders appear here the moment someone checks out on your store."}
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Order</th>
                  <th className="px-4 py-2.5 font-medium">Customer</th>
                  <th className="hidden px-4 py-2.5 font-medium sm:table-cell">Items</th>
                  <th className="px-4 py-2.5 font-medium">Total</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="hidden px-4 py-2.5 font-medium md:table-cell">Fulfilment</th>
                  <th className="hidden px-4 py-2.5 font-medium lg:table-cell">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/70">
                {orders.map((o) => (
                  <tr key={o.id} className="cursor-pointer bg-zinc-950 transition hover:bg-zinc-900/50" onClick={() => setOpenId(o.id)}>
                    <td className="px-4 py-3 font-medium text-zinc-100">#{o.order_number}</td>
                    <td className="px-4 py-3">
                      <div className="text-zinc-200">{o.contact_name || o.email || "—"}</div>
                      {o.contact_name && o.email && <div className="text-xs text-zinc-500">{o.email}</div>}
                    </td>
                    <td className="hidden px-4 py-3 text-zinc-400 sm:table-cell">{o.item_count ?? "—"}</td>
                    <td className="px-4 py-3 text-zinc-200">{formatMoney(o.total_cents, o.currency)}</td>
                    <td className="px-4 py-3">
                      <Badge color={(ORDER_STATUS_COLORS[o.status] ?? "zinc") as never} size="sm">
                        {ORDER_STATUS_LABELS[o.status] ?? o.status}
                      </Badge>
                    </td>
                    <td className="hidden px-4 py-3 md:table-cell">
                      {o.fulfillment_status === "fulfilled"
                        ? <Badge color="emerald" size="sm">Shipped</Badge>
                        : o.fulfillment_status === "na"
                        ? <span className="text-xs text-zinc-600">—</span>
                        : (o.status === "paid" || o.status === "free")
                        ? <Badge color="amber" size="sm">To fulfil</Badge>
                        : <span className="text-xs text-zinc-600">—</span>}
                    </td>
                    <td className="hidden px-4 py-3 text-zinc-500 lg:table-cell">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paginator total={total} limit={PAGE_SIZE} offset={page * PAGE_SIZE} onChange={(nextOffset) => setPage(Math.floor(nextOffset / PAGE_SIZE))} />
        </>
      )}

      {openId && (
        <OrderDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />
      )}
    </div>
  );
}

// ─── Order detail drawer ────────────────────────────────────────────────────
function OrderDetail({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [order, setOrder] = useState<StoreOrder | null>(null);
  const [fulfilling, setFulfilling] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    storeApi.order(id).then((r) => setOrder(r.order))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed to load order"));
  }, [id, toast]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async () => {
    if (!(await confirm({
      title: "Mark this order as paid?",
      description: "Confirm you've received the money. This records the payment, updates inventory, and unlocks any digital content.",
      confirmLabel: "Mark paid",
    }))) return;
    await act(() => storeApi.markOrderPaid(id), "Order marked paid");
  };

  const cancel = async () => {
    if (!(await confirm({ title: "Cancel this order?", description: "The customer won't be charged.", confirmLabel: "Cancel order", danger: true }))) return;
    await act(() => storeApi.cancelOrder(id), "Order canceled");
  };

  const canMarkPaid = order && (order.status === "pending" || order.status === "enquiry");
  const canCancel = order && (order.status === "pending" || order.status === "enquiry");
  const canRefund = order && (order.status === "paid" || order.status === "partially_refunded");
  const canFulfill = order && (order.status === "paid" || order.status === "free") && order.fulfillment_status === "unfulfilled";

  return (
    <Drawer open onClose={onClose} title={order ? `Order #${order.order_number}` : "Order"} width="lg">
      {order === null ? (
        <div className="space-y-3 p-5">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : (
        <div className="flex h-full flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto p-5">
            {/* Status strip */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={(ORDER_STATUS_COLORS[order.status] ?? "zinc") as never}>
                {ORDER_STATUS_LABELS[order.status] ?? order.status}
              </Badge>
              {order.fulfillment_status === "fulfilled" && <Badge color="emerald">Shipped</Badge>}
              {order.provider && <span className="text-xs text-zinc-500">via {order.provider}</span>}
              <span className="ml-auto text-xs text-zinc-500">{fmtDate(order.created_at)}</span>
            </div>

            {/* Customer */}
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-zinc-100">{order.contact_name || order.email}</span>
                  {order.contact_name && <span className="ml-2 text-zinc-500">{order.email}</span>}
                </div>
                {order.contact_id && (
                  <Link to={`/contacts/${order.contact_id}`} className="text-xs text-rose-400 hover:underline">
                    View contact →
                  </Link>
                )}
              </div>
              {order.customer_note && (
                <p className="mt-2 border-t border-zinc-800 pt-2 text-xs text-zinc-400">
                  <span className="text-zinc-500">Note:</span> {order.customer_note}
                </p>
              )}
            </div>

            {/* Items */}
            <div className="overflow-hidden rounded-lg border border-zinc-800">
              {(order.items ?? []).map((it) => (
                <div key={it.id} className="flex items-center gap-3 border-b border-zinc-800/70 p-3 last:border-0">
                  {it.image_url ? (
                    <img src={it.image_url} alt="" className="h-10 w-10 rounded-lg border border-zinc-800 object-cover" />
                  ) : (
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-zinc-600">
                      <Package className="h-4 w-4" />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-zinc-100">{it.product_name}</div>
                    <div className="text-xs text-zinc-500">
                      {it.variant_title && <span>{it.variant_title} · </span>}
                      {it.quantity} × {formatMoney(it.unit_amount_cents, order.currency)}
                      {it.sku && <span> · {it.sku}</span>}
                    </div>
                  </div>
                  <div className="text-sm font-medium text-zinc-100">{formatMoney(it.total_cents, order.currency)}</div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="space-y-1 rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-sm">
              <Row label="Subtotal" value={formatMoney(order.subtotal_cents, order.currency)} />
              {order.discount_cents > 0 && (
                <Row label={`Discount${order.discount_code ? ` (${order.discount_code})` : ""}`} value={`−${formatMoney(order.discount_cents, order.currency)}`} />
              )}
              {(order.shipping_cents > 0 || order.shipping_rate_name) && (
                <Row label={`Shipping${order.shipping_rate_name ? ` — ${order.shipping_rate_name}` : ""}`} value={formatMoney(order.shipping_cents, order.currency)} />
              )}
              {order.tax_cents > 0 && <Row label="Tax" value={formatMoney(order.tax_cents, order.currency)} />}
              <div className="border-t border-zinc-800 pt-1">
                <Row label="Total" value={formatMoney(order.total_cents, order.currency)} bold />
              </div>
              {order.refunded_cents > 0 && (
                <Row label="Refunded" value={`−${formatMoney(order.refunded_cents, order.currency)}`} accent="text-rose-400" />
              )}
            </div>

            {/* Shipping */}
            {order.shipping_address && (
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-sm">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-zinc-500">
                  <MapPin className="h-3.5 w-3.5" /> Ship to
                </p>
                {addressLines(order.shipping_address).map((l, i) => (
                  <div key={i} className="text-zinc-300">{l}</div>
                ))}
                {order.tracking_number && (
                  <p className="mt-2 border-t border-zinc-800 pt-2 text-xs text-zinc-400">
                    Tracking: <span className="text-zinc-200">{order.tracking_number}</span>
                    {order.shipping_carrier && <span> ({order.shipping_carrier})</span>}
                    {order.tracking_url && (
                      <a href={order.tracking_url} target="_blank" rel="noreferrer" className="ml-2 text-rose-400 hover:underline">Track →</a>
                    )}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="border-t border-zinc-800 px-5 py-3">
            <div className="flex flex-wrap justify-end gap-2">
              {canMarkPaid && (
                <Button size="sm" variant="secondary" leftIcon={<CheckCircle2 className="h-4 w-4" />} onClick={markPaid} disabled={busy}>
                  Mark paid
                </Button>
              )}
              {canFulfill && (
                <Button size="sm" leftIcon={<Truck className="h-4 w-4" />} onClick={() => setFulfilling(true)} disabled={busy}>
                  Fulfil order
                </Button>
              )}
              {canRefund && (
                <Button size="sm" variant="secondary" leftIcon={<RefreshCcw className="h-4 w-4" />} onClick={() => setRefunding(true)} disabled={busy}>
                  Refund
                </Button>
              )}
              {(order.status === "paid" || order.status === "free") && (
                <Button size="sm" variant="ghost" leftIcon={<Mail className="h-4 w-4" />} disabled={busy}
                  onClick={() => act(() => storeApi.resendReceipt(id), "Receipt sent")}>
                  Resend receipt
                </Button>
              )}
              {canCancel && (
                <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" leftIcon={<XCircle className="h-4 w-4" />} onClick={cancel} disabled={busy}>
                  Cancel
                </Button>
              )}
            </div>
          </div>

          {fulfilling && (
            <FulfillModal
              onClose={() => setFulfilling(false)}
              onDone={() => { setFulfilling(false); load(); onChanged(); }}
              orderId={id}
            />
          )}
          {refunding && (
            <RefundModal
              onClose={() => setRefunding(false)}
              onDone={() => { setRefunding(false); load(); onChanged(); }}
              orderId={id}
              maxCents={order.total_cents - order.refunded_cents}
              currency={order.currency}
            />
          )}
        </div>
      )}
    </Drawer>
  );
}

function Row({ label, value, bold, accent }: { label: string; value: string; bold?: boolean; accent?: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "font-medium text-zinc-100" : "text-zinc-400"}>{label}</span>
      <span className={accent ?? (bold ? "font-semibold text-zinc-100" : "text-zinc-300")}>{value}</span>
    </div>
  );
}

function FulfillModal({ orderId, onClose, onDone }: { orderId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tracking_number: "", tracking_url: "", carrier: "", notify: true });

  const submit = async () => {
    setSaving(true);
    try {
      await storeApi.fulfillOrder(orderId, {
        tracking_number: form.tracking_number || null,
        tracking_url: form.tracking_url || null,
        carrier: form.carrier || null,
        notify: form.notify,
      });
      toast.success("Order fulfilled");
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Fulfil order" size="md">
      <div className="space-y-4 p-5">
        <div>
          <Label htmlFor="f-track">Tracking number</Label>
          <Input id="f-track" value={form.tracking_number} onChange={(e) => setForm((f) => ({ ...f, tracking_number: e.target.value }))} placeholder="optional" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="f-carrier">Carrier</Label>
            <Input id="f-carrier" value={form.carrier} onChange={(e) => setForm((f) => ({ ...f, carrier: e.target.value }))} placeholder="USPS, UPS…" />
          </div>
          <div>
            <Label htmlFor="f-url">Tracking URL</Label>
            <Input id="f-url" value={form.tracking_url} onChange={(e) => setForm((f) => ({ ...f, tracking_url: e.target.value }))} placeholder="optional" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={form.notify} onChange={(e) => setForm((f) => ({ ...f, notify: e.target.checked }))} className="accent-rose-500" />
          Email the customer their shipping confirmation
        </label>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving}>Mark fulfilled</Button>
      </ModalFooter>
    </Modal>
  );
}

function RefundModal({ orderId, maxCents, currency, onClose, onDone }: {
  orderId: string; maxCents: number; currency: string; onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState(centsToStr(maxCents));

  const submit = async () => {
    const cents = strToCents(amount);
    if (cents < 1 || cents > maxCents) {
      toast.error(`Enter an amount up to ${formatMoney(maxCents, currency)}.`);
      return;
    }
    setSaving(true);
    try {
      await storeApi.refundOrder(orderId, cents);
      toast.success("Refund recorded");
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Refund failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Refund order" size="sm">
      <div className="space-y-4 p-5">
        <div>
          <Label htmlFor="rf-amt">Amount ({currency})</Label>
          <Input id="rf-amt" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          <FieldHint>
            Up to {formatMoney(maxCents, currency)}. Card payments are refunded through your payment
            provider; manual orders are just recorded.
          </FieldHint>
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving}>Refund</Button>
      </ModalFooter>
    </Modal>
  );
}
